import { API_METHODS } from 'constants/general';
import { MATERIALITY_SECTIONS_LIST, MATERIALITY_SECTIONS_LIST_DETAILS } from 'constants/requestUrls';
import mainApi from 'store/apis/mainApi';
import { LAYOUTS_MA_RECORD, LAYOUTS_MA_RECORD_TYPE, LAYOUTS_MA_RECORD_TYPE_DETAILS } from 'store/tags/layoutsMA';

import type IMaterialStakeholderListStatus from 'store/slices/materialAssessment/interfaces/IMaterialStakeholderListStatus';
import type IMaterialStakeholdersListStatusRequest from 'store/slices/materialAssessment/interfaces/IMaterialStakeholdersListStatusRequest';
import type { IBaseQueryPaginationResponse } from 'store/types/IBaseQueryPaginationResponse';
import type IBaseQueryResponse from 'store/types/IBaseQueryResponse';
import type ISectionItem from '../interfaces/ISectionItem';
import type ISectionMADetailsRequest from '../interfaces/ISectionMADetailsRequest';
import type ISectionMARequest from '../interfaces/ISectionMARequest';
import type IUpdateSectionMADetailsRequest from '../interfaces/IUpdateSectionMADetailsRequest';

export const layoutsMAterialityApi = mainApi.injectEndpoints({
  endpoints: (build) => ({
    getSectionsMA: build.query<
    IBaseQueryPaginationResponse<ISectionItem[]>, ISectionMARequest>({
      query: ({
        maId,
      }) => ({
        url: MATERIALITY_SECTIONS_LIST.replace('{maId}', maId),
      }),
      providesTags: (result) => (
        result?.data?.items?.length && result?.data?.items?.length > 0
          ? [...result.data.items.map(({ id }) => (
            { type: LAYOUTS_MA_RECORD, id } as const)), LAYOUTS_MA_RECORD]
          : [LAYOUTS_MA_RECORD]
      ),
    }),
    getSectionsDetailsMA: build.query<
    IBaseQueryResponse<ISectionItem>, ISectionMADetailsRequest>({
      query: ({ maId, type, vcmId }) => ({
        url: MATERIALITY_SECTIONS_LIST_DETAILS.replace('{maId}', maId).replace('{type}', type),
        params: {
          ...(vcmId ? { valueChainMapId: vcmId as number } : { }),
        },
      }),
      providesTags: [LAYOUTS_MA_RECORD_TYPE],
    }),
    updateSectionMA: build.mutation<IBaseQueryResponse<ISectionItem>,
    IUpdateSectionMADetailsRequest>({
      query: ({
        maId, type, data, valueChainMapId,
      }) => ({
        url: MATERIALITY_SECTIONS_LIST_DETAILS.replace('{maId}', maId).replace('{type}', type),
        method: API_METHODS.patch,
        body: data,
        params: {
          ...(valueChainMapId ? { valueChainMapId } : { }),
        },
      }),
      invalidatesTags: [LAYOUTS_MA_RECORD, LAYOUTS_MA_RECORD_TYPE, LAYOUTS_MA_RECORD_TYPE_DETAILS],
    }),
    updateDefaultSectionMA: build.mutation<IBaseQueryResponse<ISectionItem>,
    IUpdateSectionMADetailsRequest>({
      query: ({ maId, type, data }) => ({
        url: MATERIALITY_SECTIONS_LIST_DETAILS.replace('{maId}', maId).replace('{type}', type),
        method: API_METHODS.patch,
        body: data,
      }),
      invalidatesTags: [LAYOUTS_MA_RECORD, LAYOUTS_MA_RECORD_TYPE, LAYOUTS_MA_RECORD_TYPE_DETAILS],
    }),
    getMaterialityStakeholdersListStatus: build.query<
    IBaseQueryResponse<IMaterialStakeholderListStatus>, IMaterialStakeholdersListStatusRequest>({
      query: ({
        maId, type, vcmId,
      }) => ({
        url: MATERIALITY_SECTIONS_LIST_DETAILS.replace('{maId}', `${maId}`).replace('{type}', `${type}`),
        params: {
          ...(vcmId ? { valueChainMapId: vcmId as number } : { }),
        },
      }),
      providesTags: [LAYOUTS_MA_RECORD_TYPE_DETAILS],
    }),
  }),
});

export const {
  useGetSectionsMAQuery,
  useGetSectionsDetailsMAQuery,
  useLazyGetSectionsDetailsMAQuery,
  useUpdateSectionMAMutation,
  useUpdateDefaultSectionMAMutation,
  useGetMaterialityStakeholdersListStatusQuery,
} = layoutsMAterialityApi;
